import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import {
  SessionUser, getSession, setSession, getToken,
  logout as doLogout, startPreview, fetchMe, login as doLogin, LoginResult,
} from "@/api/auth";
import { MembershipTier } from "@/types/api";

interface SessionCtx {
  user: SessionUser | null;
  isAuthed: boolean;          // any session (preview or real)
  isAuthenticated: boolean;   // real token-based, /auth/me-validated session
  loading: boolean;           // true while validating an existing token on load
  tier: MembershipTier | null;
  enterPreview: (email?: string) => void;
  login: (identifier: string, password: string) => Promise<LoginResult>;
  logout: () => void;
}

const Ctx = createContext<SessionCtx>({
  user: null, isAuthed: false, isAuthenticated: false, loading: false, tier: null,
  enterPreview: () => {}, login: async () => ({ ok: false, reason: "network" }), logout: () => {},
});

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SessionUser | null>(() => getSession());
  // If a real token is present, validate it against /auth/me before trusting tier.
  const [loading, setLoading] = useState<boolean>(() => !!getToken());

  useEffect(() => {
    let alive = true;
    if (!getToken()) return;
    fetchMe().then((u) => {
      if (!alive) return;
      if (u) {
        setUser(u);
        setSession(u);
      } else {
        // token invalid/expired and refresh failed -> clear real auth, keep any preview session
        doLogout();
        setUser((prev) => (prev && prev.preview ? prev : null));
      }
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const enterPreview = useCallback((email?: string) => {
    setUser(startPreview(email));
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    const res = await doLogin(identifier, password);
    if (res.ok) setUser(res.user);
    return res;
  }, []);

  const logout = useCallback(() => {
    void doLogout();
    setUser(null);
  }, []);

  const value: SessionCtx = {
    user,
    isAuthed: !!user,
    isAuthenticated: !!user?.authenticated,
    loading,
    tier: (user?.tier as MembershipTier) ?? null,
    enterPreview,
    login,
    logout,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useSession() {
  return useContext(Ctx);
}
